/**
 * ConnectionStatusIndicator - Visual connection status for DJ Live
 * Phase DL-4
 * 
 * Displays connection state in a discreet, accessible manner:
 * - Connected: Green dot (or hidden for minimal UI)
 * - Reconnecting: Yellow pulsing indicator
 * - Disconnected: Red indicator with message
 * 
 * Designed to be non-distracting during live events while clearly
 * communicating connection issues when they occur.
 */

import React from 'react';
import { Wifi, WifiOff, Loader2 } from 'lucide-react';
import { CONNECTION_STATES } from '../hooks/useConnectionStatus';

/**
 * ConnectionStatusIndicator Component
 * 
 * @param {Object} props
 * @param {string} props.status - Connection status from useConnectionStatus
 * @param {boolean} props.isRecovering - Whether currently recovering from disconnection
 * @param {boolean} props.isStale - Whether the displayed data is stale
 * @param {string} props.variant - 'minimal' | 'standard' | 'tv' (display style)
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.showWhenConnected - Show indicator even when connected
 * @param {Date} props.lastUpdate - Last successful update timestamp
 */
export function ConnectionStatusIndicator({
  status,
  isRecovering = false,
  isStale = false,
  variant = 'standard',
  className = '',
  showWhenConnected = false,
  lastUpdate
}) {
  // Don't show anything if connected and not configured to show when connected
  if (status === CONNECTION_STATES.CONNECTED && !showWhenConnected && !isRecovering) {
    return null;
  }
  
  // Determine display state
  const getStatusConfig = () => {
    if (isRecovering) {
      return {
        icon: Loader2,
        iconClass: 'animate-spin text-green-400',
        bgClass: 'bg-green-500/20 border-green-500/30',
        textClass: 'text-green-400',
        label: 'Reconnected',
        description: 'Syncing...'
      };
    }
    
    switch (status) {
      case CONNECTION_STATES.CONNECTED:
        return {
          icon: Wifi,
          iconClass: 'text-green-400',
          bgClass: 'bg-green-500/20 border-green-500/30',
          textClass: 'text-green-400',
          label: 'Connected',
          description: null
        };
        
      case CONNECTION_STATES.RECONNECTING:
        return {
          icon: Wifi,
          iconClass: 'text-yellow-400 animate-pulse',
          bgClass: 'bg-yellow-500/20 border-yellow-500/30',
          textClass: 'text-yellow-400',
          label: 'Reconnecting',
          description: 'Updates paused...'
        };
        
      case CONNECTION_STATES.DISCONNECTED:
        return {
          icon: WifiOff,
          iconClass: 'text-red-400',
          bgClass: 'bg-red-500/20 border-red-500/30',
          textClass: 'text-red-400',
          label: 'Connection Lost',
          description: 'Showing last known data'
        };
        
      default:
        return null;
    }
  };
  
  const config = getStatusConfig();
  if (!config) return null;
  
  const Icon = config.icon;
  
  // Minimal variant - just a dot indicator
  if (variant === 'minimal') {
    return (
      <div 
        className={`flex items-center gap-1.5 ${className}`}
        role="status"
        aria-live="polite"
        aria-label={`Connection status: ${config.label}`}
      >
        <span 
          className={`w-2 h-2 rounded-full ${
            status === CONNECTION_STATES.CONNECTED ? 'bg-green-400' :
            status === CONNECTION_STATES.RECONNECTING ? 'bg-yellow-400 animate-pulse' :
            'bg-red-400'
          }`}
        />
        {status !== CONNECTION_STATES.CONNECTED && (
          <span className={`text-xs ${config.textClass}`}>
            {config.label}
          </span>
        )}
      </div>
    );
  }
  
  // TV variant - optimized for large displays, non-intrusive
  if (variant === 'tv') {
    // Only show when there's a problem
    if (status === CONNECTION_STATES.CONNECTED && !isRecovering) {
      return null;
    }
    
    return (
      <div 
        className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 ${className}`}
        role="status"
        aria-live="polite"
      >
        <div className={`
          flex items-center gap-2 px-4 py-2 rounded-full
          ${config.bgClass} border backdrop-blur-sm
          shadow-lg transition-all duration-300
        `}>
          <Icon className={`w-4 h-4 ${config.iconClass}`} />
          <span className={`text-sm font-medium ${config.textClass}`}>
            {config.label}
          </span>
          {config.description && (
            <span className={`text-xs opacity-75 ${config.textClass}`}>
              • {config.description}
            </span>
          )}
        </div>
      </div>
    );
  }
  
  // Standard variant - full indicator with icon and text
  return (
    <div 
      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${config.bgClass} ${className}`}
      role="status"
      aria-live="polite"
      aria-label={`Connection status: ${config.label}`}
    >
      <Icon className={`w-4 h-4 ${config.iconClass}`} />
      <div className="flex flex-col">
        <span className={`text-sm font-medium ${config.textClass}`}>
          {config.label}
        </span>
        {config.description && (
          <span className={`text-xs opacity-75 ${config.textClass}`}>
            {config.description}
          </span>
        )}
      </div>
      {lastUpdate && isStale && (
        <span className="text-xs text-slate-400 ml-2">
          Last update: {formatTimeSince(lastUpdate)}
        </span>
      )}
    </div>
  );
}

/**
 * Format time since last update for display
 */
function formatTimeSince(timestamp) {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  return `${Math.floor(seconds / 3600)}h ago`;
}

/**
 * ConnectionStatusBadge - Compact badge for headers/toolbars
 */
export function ConnectionStatusBadge({ status, isRecovering, className = '' }) {
  if (status === CONNECTION_STATES.CONNECTED && !isRecovering) {
    return null;
  }
  
  const getConfig = () => {
    if (isRecovering) {
      return { color: 'bg-green-500', pulse: true, text: 'Syncing' };
    }
    switch (status) {
      case CONNECTION_STATES.RECONNECTING:
        return { color: 'bg-yellow-500', pulse: true, text: 'Reconnecting' };
      case CONNECTION_STATES.DISCONNECTED:
        return { color: 'bg-red-500', pulse: false, text: 'Offline' };
      default:
        return null;
    }
  };
  
  const config = getConfig();
  if (!config) return null;
  
  return (
    <span 
      className={`
        inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium
        ${config.color} text-white ${config.pulse ? 'animate-pulse' : ''} ${className}
      `}
      role="status"
      aria-live="polite"
    >
      <span className="w-1.5 h-1.5 rounded-full bg-white/50" />
      {config.text}
    </span>
  );
}

export default ConnectionStatusIndicator;
